// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CurrencyProvider } from './components/internationalization/MultiCurrencySupport';
import { CartProvider } from './components/ecommerce/ShoppingCart';
import { AuthProvider } from './contexts/AuthContext';
import Header from './components/common/Header';
import UpdatedFooter from './components/common/Footer';
import Home from './pages/Home';
import Products from './pages/Products';
import About from './pages/About';
import Contact from './pages/Contact';
import ProductDetailPage from './pages/ProductDetailPage';
import Admin from './pages/Admin';
import PaymentGateway from './components/payments/PaymentGateway';
import BlogPage from './pages/BlogPage';
import HomeDecorProducts from './pages/HomeDecorProducts';
import ResinFormulaPage from './pages/ResinFormulaPage';
import UserDashboard from './components/auth/UserDashboard';
import './styles/globals.css';

function App() {
  return (
    <CurrencyProvider>
      <CartProvider>
        <AuthProvider>
          <Router>
            <AnimatePresence mode="wait">
              <div className="App">
                <Routes>
                  {/* Admin Route - No Header/Footer */}
                  <Route path="/admin" element={<Admin />} />

                  {/* Payment Route - No Header/Footer */}
                  <Route path="/payment" element={
                    <PaymentGateway
                      orderDetails={{
                        items: [
                          {
                            id: 1,
                            name: "Azure River Dining Table",
                            image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=150&h=150&fit=crop",
                            price: 3200,
                            quantity: 1
                          },
                          {
                            id: 2,
                            name: "Ocean Wave Coffee Table",
                            image: "https://images.unsplash.com/photo-1506439773649-6e0eb8cfb237?w=150&h=150&fit=crop",
                            price: 1850,
                            quantity: 1
                          }
                        ],
                        subtotal: 5050,
                        shipping: 0,
                        tax: 909,
                        total: 5959
                      }}
                      onPaymentSuccess={() => window.location.href = '/'}
                      onPaymentCancel={() => window.location.href = '/products'}
                    />
                  } />

                  {/* Public Routes - With Header/Footer */}
                  <Route path="/*" element={
                    <>
                      <Header />
                      <main className="min-h-screen">
                        <Routes>
                          <Route path="/" element={<Home />} />
                          <Route path="/products" element={<Products />} />
                          <Route path="/products/:id" element={<ProductDetailPage />} />
                          <Route path="/about" element={<About />} />
                          <Route path="/contact" element={<Contact />} />
                          <Route path="/blog" element={<BlogPage />} />
                          <Route path="/resin-formulas" element={<ResinFormulaPage />} />
                          <Route path="/home-decor" element={<HomeDecorProducts />} />
                          <Route path="/dashboard" element={<UserDashboard />} />

                          {/* 404 Page */}
                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                      </main>
                      <UpdatedFooter />
                    </>
                  } />
                </Routes>
              </div>
            </AnimatePresence>
          </Router>
        </AuthProvider>
      </CartProvider>
    </CurrencyProvider>
  );
}

// Simple 404 Page Component
const NotFoundPage = () => {
  return (
    <div className="min-h-screen bg-dark-950 pt-20 flex items-center justify-center">
      <div className="text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-9xl font-bold text-accent-400 mb-4">404</h1>
          <h2 className="text-3xl font-bold text-white mb-4">Page Not Found</h2>
          <p className="text-dark-300 mb-8">
            The page you're looking for doesn't exist or has been moved.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a
              href="/"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-accent-500 to-accent-600 text-white font-medium rounded-lg hover:from-accent-600 hover:to-accent-700 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Go Home
            </motion.a>
            <motion.a
              href="/payment"
              className="inline-flex items-center px-8 py-4 border border-accent-500 text-accent-400 font-medium rounded-lg hover:bg-accent-500/10 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Payment Demo
            </motion.a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default App;